import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchUserPicker,
  loginWorker,
  loginAdmin,
  loginStaff,
  checkSession,
} from '../services/auth';
import { useAuth } from '../context/AuthContext';
import { preferredView, desktopHome } from '../services/viewMode';

const STAFF_ROLES = ['OfficeStaff', 'Accounting', 'ProjectManager'];
function staffLandingPage(role) {
  // PM & Accounting have both a desktop layout and the mobile app — send them to the
  // one matching their device (or their saved choice). OfficeStaff is desktop-only.
  const canMobile = role === 'ProjectManager' || role === 'Accounting';
  if (canMobile && preferredView() === 'mobile') return '/dashboard';
  return desktopHome(role);
}

export default function Login() {
  const navigate = useNavigate();
  const auth = useAuth();

  // 'checking' = validating any stored session on mount, before showing anything
  const [phase, setPhase] = useState('checking');
  // 'picker' | 'pin' | 'admin'
  const [view, setView]   = useState('picker');

  const [users, setUsers]           = useState([]);
  const [usersError, setUsersError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pin, setPin]               = useState('');
  const [pinError, setPinError]     = useState(null);
  const [pinLoading, setPinLoading] = useState(false);

  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminError, setAdminError]       = useState(null);
  const [adminLoading, setAdminLoading]   = useState(false);

  const [staffUsername, setStaffUsername] = useState('');
  const [staffPassword, setStaffPassword] = useState('');
  const [staffError, setStaffError]       = useState(null);
  const [staffLoading, setStaffLoading]   = useState(false);

  // On mount: if there's a stored session, validate it against the server.
  // If still valid, skip straight past the login screen entirely.
  useEffect(() => {
    (async () => {
      const user = await checkSession();
      if (user) {
        if (user.role === 'admin') {
          auth.loginAdmin();
          navigate('/admin', { replace: true });
        } else if (STAFF_ROLES.includes(user.role)) {
          auth.loginStaff(user);
          navigate(staffLandingPage(user.role), { replace: true });
        } else {
          auth.login(user);
          navigate('/dashboard', { replace: true });
        }
        return;
      }
      setPhase('ready');
      loadPicker();
    })();
  }, [navigate]);

  async function loadPicker() {
    try {
      const list = await fetchUserPicker();
      setUsers(list);
    } catch (e) {
      setUsersError(e.message);
    }
  }

  function pickUser(user) {
    setSelectedUser(user);
    setPin('');
    setPinError(null);
    setView('pin');
  }

  async function submitPin() {
    if (pin.length !== 4) {
      setPinError('Enter your 4-digit PIN');
      return;
    }
    setPinLoading(true);
    setPinError(null);
    try {
      const user = await loginWorker(selectedUser.id, pin);
      auth.login(user);
      navigate('/dashboard', { replace: true });
    } catch (e) {
      setPinError(e.message);
      setPin('');
    } finally {
      setPinLoading(false);
    }
  }

  async function submitAdmin(e) {
    e.preventDefault();
    setAdminLoading(true);
    setAdminError(null);
    try {
      await loginAdmin(adminUsername, adminPassword);
      auth.loginAdmin();
      navigate('/admin', { replace: true });
    } catch (e) {
      setAdminError(e.message);
    } finally {
      setAdminLoading(false);
    }
  }

  async function submitStaff(e) {
    e.preventDefault();
    setStaffLoading(true);
    setStaffError(null);
    try {
      const user = await loginStaff(staffUsername, staffPassword);
      auth.loginStaff(user);
      navigate(staffLandingPage(user.role), { replace: true });
    } catch (e) {
      setStaffError(e.message);
    } finally {
      setStaffLoading(false);
    }
  }

  if (phase === 'checking') {
    return (
      <div className="login-page login-page--loading">
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-header">
        <h1>Titan Electric</h1>
        <p>Field Worker Portal</p>
      </div>

      {view === 'picker' && (
        <div className="login-picker">
          <h2>Who are you?</h2>

          {usersError && (
            <div className="login-error">
              Could not load the team list — {usersError}
            </div>
          )}

          {!usersError && users.length === 0 && (
            <p className="login-loading-text">Loading team…</p>
          )}

          <div className="login-user-list">
            {users.map(u => (
              <button
                key={u.id}
                className="login-user-row"
                onClick={() => pickUser(u)}
              >
                <span className="login-user-name">{u.firstName} {u.lastName}</span>
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 8 }}>
            <button
              className="login-admin-link"
              onClick={() => { setView('staff'); setStaffError(null); }}
            >
              Staff login
            </button>
            <button
              className="login-admin-link"
              onClick={() => { setView('admin'); setAdminError(null); }}
            >
              Admin login
            </button>
          </div>
        </div>
      )}

      {view === 'pin' && selectedUser && (
        <div className="login-pin">
          <button className="login-back" onClick={() => setView('picker')}>
            ← Back
          </button>

          <h2>Hi {selectedUser.firstName}</h2>
          <p>Enter your 4-digit PIN</p>

          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            autoFocus
            value={pin}
            onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
            onKeyDown={e => e.key === 'Enter' && submitPin()}
            className="login-pin-input"
          />

          {pinError && <div className="login-error">{pinError}</div>}

          <button
            className="login-submit"
            onClick={submitPin}
            disabled={pinLoading || pin.length !== 4}
          >
            {pinLoading ? 'Checking…' : 'Log in'}
          </button>
        </div>
      )}

      {view === 'staff' && (
        <form className="login-admin-form" onSubmit={submitStaff}>
          <button type="button" className="login-back" onClick={() => setView('picker')}>← Back</button>
          <h2>Staff login</h2>
          <input
            type="text"
            placeholder="Username"
            autoComplete="username"
            value={staffUsername}
            onChange={e => setStaffUsername(e.target.value)}
            className="login-text-input"
          />
          <input
            type="password"
            placeholder="Password"
            autoComplete="current-password"
            value={staffPassword}
            onChange={e => setStaffPassword(e.target.value)}
            className="login-text-input"
          />
          {staffError && <div className="login-error">{staffError}</div>}
          <button
            type="submit"
            className="login-submit"
            disabled={staffLoading || !staffUsername || !staffPassword}
          >
            {staffLoading ? 'Checking…' : 'Log in'}
          </button>
        </form>
      )}

      {view === 'admin' && (
        <form className="login-admin-form" onSubmit={submitAdmin}>
          <button
            type="button"
            className="login-back"
            onClick={() => setView('picker')}
          >
            ← Back
          </button>

          <h2>Admin login</h2>

          <input
            type="text"
            placeholder="Username"
            autoComplete="username"
            value={adminUsername}
            onChange={e => setAdminUsername(e.target.value)}
            className="login-text-input"
          />
          <input
            type="password"
            placeholder="Password"
            autoComplete="current-password"
            value={adminPassword}
            onChange={e => setAdminPassword(e.target.value)}
            className="login-text-input"
          />

          {adminError && <div className="login-error">{adminError}</div>}

          <button
            type="submit"
            className="login-submit"
            disabled={adminLoading || !adminUsername || !adminPassword}
          >
            {adminLoading ? 'Checking…' : 'Log in'}
          </button>
        </form>
      )}
    </div>
  );
}
